// =============================================================================
// MeuDriver — Hook: Exportação CSV para admin
// =============================================================================

import { useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Previne CSV Injection (ALTO-001): campos que iniciam com =, +, -, @
 * são prefixados com apóstrofo para que o Excel os trate como texto.
 */
function sanitizeCsvField(value: string): string {
  if (/^[=+\-@]/.test(value)) return `'${value}`
  return value
}

/**
 * Separador ponto-e-vírgula para compatibilidade com Excel pt-BR (MÉDIO-004).
 * Campos são escapados (aspas duplas) e sanitizados contra CSV Injection.
 */
function convertToCSV<T extends Record<string, unknown>>(data: T[]): string {
  if (!data.length) return ''
  const headers = Object.keys(data[0])
  const rows = data.map((row) =>
    headers
      .map((h) => {
        const raw = sanitizeCsvField(String(row[h] ?? ''))
        return `"${raw.replace(/"/g, '""')}"`
      })
      .join(';'),
  )
  return [headers.join(';'), ...rows].join('\n')
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Tipos de retorno das RPCs ────────────────────────────────────────────────

interface BookingExportRow {
  booking_id: string
  student_name: string
  instructor_name: string
  scheduled_date: string
  status: string
  total_amount: number
  platform_fee: number
  instructor_payout: number
  payment_status: string
  payment_method: string
  vehicle_type: string
  created_at: string
}

interface UserExportRow {
  user_id: string
  full_name: string
  email: string
  role: string
  status: string
  created_at: string
  total_bookings: number
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Exporta bookings de um período como CSV */
export function useExportBookingsCsv() {
  return useMutation({
    mutationFn: async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      const { data, error } = await supabase.rpc('admin_export_bookings', {
        p_start_date: startDate,
        p_end_date: endDate,
      })
      if (error) throw new Error(error.message)

      const rows = (data ?? []) as BookingExportRow[]
      if (!rows.length) {
        toast.info('Nenhum booking encontrado para o período selecionado.')
        return
      }

      const csv = convertToCSV(rows as unknown as Record<string, unknown>[])
      downloadCsv(csv, `meudriver-bookings-${startDate}-${endDate}.csv`)
      toast.success(`${rows.length} registros exportados com sucesso!`)
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Erro ao exportar bookings.'
      toast.error(msg)
    },
  })
}

/** Exporta todos os usuários como CSV */
export function useExportUsersCsv() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('admin_export_users')
      if (error) throw new Error(error.message)

      const rows = (data ?? []) as UserExportRow[]
      if (!rows.length) {
        toast.info('Nenhum usuário encontrado.')
        return
      }

      const csv = convertToCSV(rows as unknown as Record<string, unknown>[])
      const today = new Date().toISOString().slice(0, 10)
      downloadCsv(csv, `meudriver-usuarios-${today}.csv`)
      toast.success(`${rows.length} usuários exportados com sucesso!`)
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Erro ao exportar usuários.'
      toast.error(msg)
    },
  })
}
